import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

const STAT_COLORS: Record<string, string> = {
  coding: 'var(--color-stat-coding)', art: 'var(--color-stat-art)',
  sound: 'var(--color-stat-sound)', design: 'var(--color-stat-design)',
};

/**
 * Training dialog — shows all staff at a glance with inline stat bars.
 * Click a training button to assign instantly; dialog stays open.
 * "Train Team" button to batch-train an entire team.
 */
export function openTrainStaffDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Train Staff',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:6px;min-width:420px;max-height:500px;overflow-y:auto;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;color:var(--color-success);min-height:16px;text-align:center;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();

    const allStaff = state.staff.filter((s) => !s.isCEO);
    if (allStaff.length === 0) {
      const msg = document.createElement('p');
      msg.textContent = 'Hire staff first — the CEO manages, not trains.';
      msg.style.cssText = 'color:var(--color-text-muted);text-align:center;padding:16px 0;';
      container.appendChild(msg);
      return;
    }

    // Team batch-train buttons
    if (state.teams.length > 0) {
      const batchRow = document.createElement('div');
      batchRow.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;margin-bottom:4px;';

      for (const team of state.teams) {
        const members = team.memberIds
          .map((id) => state.staff.find((s) => s.id === id))
          .filter((s): s is NonNullable<typeof s> => !!s && !s.isCEO && !s.isTraining && !s.isOnLeave && s.trainCooldownTicks <= 0);
        if (members.length === 0) continue;

        const btn = document.createElement('button');
        btn.className = 'btn';
        btn.textContent = `Train ${team.name} (${members.length})`;
        btn.style.cssText = 'font-size:10px;padding:4px 8px;';
        btn.addEventListener('click', () => {
          let trained = 0;
          for (const m of members) {
            // Find best affordable training for their weakest stat
            const weakest = Object.entries(m.stats).sort((a, b) => a[1] - b[1])[0]?.[0];
            const training = theme.entities.trainingOptions.find(
              (t) => t.targetStat === weakest && m.level >= t.minLevel && t.cost <= store.getState().company.funds,
            );
            if (training) {
              const r = store.dispatch({ type: 'TRAIN_STAFF', staffId: m.id, trainingId: training.id });
              if (r.success) trained++;
            }
          }
          statusEl.textContent = trained > 0 ? `${trained} staff sent to training` : 'No affordable training available';
          render();
        });
        batchRow.appendChild(btn);
      }

      if (batchRow.children.length > 0) {
        const label = document.createElement('div');
        label.textContent = 'BATCH TRAIN';
        label.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:2px;';
        container.appendChild(label);
        container.appendChild(batchRow);
      }
    }

    // Each staff member as a compact row
    const maxStat = Math.max(30, ...allStaff.flatMap((s) => Object.values(s.stats)));

    for (const staff of allStaff) {
      const row = document.createElement('div');
      row.className = 'card';
      row.style.cssText = 'padding:6px 8px;display:flex;flex-direction:column;gap:3px;';

      // Header: name + status
      const header = document.createElement('div');
      header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';

      const nameEl = document.createElement('span');
      nameEl.style.cssText = 'font-size:12px;font-weight:600;';
      const roleDef = theme.entities.staffRoles.find((r) => r.id === staff.role) ?? theme.entities.founderRole;
      nameEl.textContent = `${staff.name}`;

      const metaEl = document.createElement('span');
      metaEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
      metaEl.textContent = `${roleDef.name} · Lv.${staff.level}`;

      header.appendChild(nameEl);
      header.appendChild(metaEl);
      row.appendChild(header);

      // Stat mini-bars (all 4 in one row)
      const statsRow = document.createElement('div');
      statsRow.style.cssText = 'display:flex;gap:3px;';

      for (const [stat, value] of Object.entries(staff.stats)) {
        const col = document.createElement('div');
        col.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:1px;';
        const lbl = document.createElement('span');
        lbl.textContent = `${stat.slice(0, 3)} ${Math.round(value)}`;
        lbl.style.cssText = `font-size:9px;color:${STAT_COLORS[stat] ?? 'var(--color-text-muted)'};`;
        const bar = document.createElement('div');
        bar.style.cssText = `height:4px;background:var(--color-bg-primary);border-radius:2px;overflow:hidden;`;
        const fill = document.createElement('div');
        fill.style.cssText = `height:100%;width:${(value / maxStat) * 100}%;background:${STAT_COLORS[stat] ?? '#888'};border-radius:2px;`;
        bar.appendChild(fill);
        col.appendChild(lbl);
        col.appendChild(bar);
        statsRow.appendChild(col);
      }
      row.appendChild(statsRow);

      // Status or training options
      if (staff.isTraining) {
        const tag = document.createElement('div');
        tag.style.cssText = 'font-size:10px;color:var(--color-warning);';
        tag.textContent = `Training ${staff.trainingTarget ?? ''}...`;
        row.appendChild(tag);
      } else if (staff.isOnLeave) {
        const tag = document.createElement('div');
        tag.style.cssText = 'font-size:10px;color:var(--color-info);';
        tag.textContent = 'On leave';
        row.appendChild(tag);
      } else if (staff.trainCooldownTicks > 0) {
        const tag = document.createElement('div');
        tag.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
        tag.textContent = 'Cooldown';
        row.appendChild(tag);
      } else {
        // Training buttons — one per available training
        const options = theme.entities.trainingOptions.filter(
          (t) => staff.level >= t.minLevel,
        );
        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'display:flex;gap:3px;flex-wrap:wrap;';

        for (const t of options) {
          const canAfford = t.cost <= store.getState().company.funds;
          const btn = document.createElement('button');
          btn.className = 'btn';
          btn.disabled = !canAfford;
          btn.style.cssText = `font-size:9px;padding:2px 6px;color:${STAT_COLORS[t.targetStat] ?? 'inherit'};border-color:${STAT_COLORS[t.targetStat] ?? 'var(--border-color)'};`;
          btn.textContent = `${t.targetStat.slice(0, 3)} +${t.statGain} (${formatCurrency(t.cost)})`;
          btn.title = `${t.name}: +${t.statGain} ${t.targetStat}, ${formatCurrency(t.cost)}`;
          btn.addEventListener('click', () => {
            const r = store.dispatch({ type: 'TRAIN_STAFF', staffId: staff.id, trainingId: t.id });
            if (r.success) {
              statusEl.textContent = `${staff.name} → ${t.name}`;
            } else {
              statusEl.textContent = r.error ?? 'Failed';
              statusEl.style.color = 'var(--color-danger)';
            }
            render();
          });
          btnRow.appendChild(btn);
        }
        row.appendChild(btnRow);
      }

      container.appendChild(row);
    }
  }

  render();
  dialog.body.appendChild(container);
  dialog.body.appendChild(statusEl);
  dialog.mount(parent);
}
